import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/labels';
import { Button, Card, ErrorText } from '@/components/ui';
import { useOrgContext } from './layout';
import { formatDate } from '@/lib/dates';

const SOURCE_LABELS: Record<string, string> = {
  creator: 'Created the organization',
  domain: 'Email domain',
  invitation: 'Invitation',
  invite_code: 'Invite code',
  csv: 'Bulk upload',
  join_request: 'Approved request',
  sso: 'Single sign-on',
};

export function OrgMembersPage() {
  const { org, usage, canManage } = useOrgContext();
  const api = useApi();
  const [descendants, setDescendants] = useState(false);
  const hasChildren = Boolean(usage && usage.usage.maxChildOrgs > 0) && !org.parentId;
  const members = useQuery({
    queryKey: ['members', org.id, descendants],
    queryFn: () => api.members(org.id, descendants),
  });

  return (
    <Card
      title={members.data ? `${members.data.members.length.toLocaleString()} members` : 'Members'}
      action={
        <div className="flex items-center gap-4">
          {hasChildren && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={descendants}
                onChange={(e) => setDescendants(e.target.checked)}
              />
              Include child organizations
            </label>
          )}
          {canManage && (
            <Link to="../invites">
              <Button variant="secondary">Invite members</Button>
            </Link>
          )}
        </div>
      }
    >
      {members.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : members.isError ? (
        <ErrorText error={members.error} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Role</th>
                <th className="py-2 pr-4 font-medium">Joined via</th>
                <th className="py-2 pr-4 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.data.members.map((m) => (
                <tr key={`${m.orgId}-${m.userId}`}>
                  <td className="py-2 pr-4">{m.email}</td>
                  <td className="py-2 pr-4">{ROLE_LABELS[m.role]}</td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {SOURCE_LABELS[m.source] ?? m.source}
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">{formatDate(m.joinedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
